import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ require two levels up to reach convex/_generated/
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import { useOrgContext } from '@/hooks/useOrgContext';
import { usePolicyForm } from '@/hooks/usePolicyForm';
import { useDriverForm } from '@/hooks/useDriverForm';
import { useEmployeePolicyForm } from '@/hooks/useEmployeePolicyForm';
import { generateCertificatePdf } from '@/lib/certificates/generateCertificate';
import { ShieldCheck, MapPin, Car } from 'lucide-react';
import type {
  IOrgInsuranceType,
  IInsuranceField,
  IInsuranceProvider,
  IFieldSectionConfig,
  IFieldSectionWithFields,
  TPolicySource,
  IPolicyFieldValueV2,
} from '@/@types/insurance';

export function usePolicyFormPage(policyId: Id<'insurancePolicies'> | null, isEditing: boolean) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlEmployeeId = searchParams.get('employeeId') ?? undefined;
  const urlCompanyId = searchParams.get('companyId') ?? undefined;
  const urlSource = searchParams.get('source') as TPolicySource | null; // AS_JUSTIFIED: validated below with fallback
  const initialSource: TPolicySource =
    urlSource === 'company_employer' ? 'company_employer' : 'personal';

  const { currentOrg, isInOrg, isLoading: orgLoading } = useOrgContext();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedTypeId, setSelectedTypeIdRaw] = useState<Id<'orgInsuranceTypes'> | null>(null);
  const [selectedBenefitPlanId, setSelectedBenefitPlanId] = useState<string>('');
  const [wizardPhase, setWizardPhase] = useState<'policy-info' | 'application'>(
    isEditing ? 'application' : 'policy-info'
  );

  const form = usePolicyForm();

  // Wrap type setter to reset provider and benefit plan when type changes
  const setSelectedTypeId = useCallback(
    (typeId: Id<'orgInsuranceTypes'>) => {
      setSelectedTypeIdRaw(prev => {
        if (prev !== typeId) {
          form.setFieldValue('Provider', '');
          setSelectedBenefitPlanId('');
        }
        return typeId;
      });
    },
    [form]
  );
  const driverForm = useDriverForm();
  const employeePolicyForm = useEmployeePolicyForm(
    currentOrg?._id,
    initialSource,
    urlCompanyId,
    urlEmployeeId
  );

  const orgTypes = useQuery(
    api['insurance/types'].listOrgTypes,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );

  const existingPolicy = useQuery(api['insurance/policies'].get, policyId ? { policyId } : 'skip');

  const certificateData = useQuery(
    api.insurance.certificateData.getCertificateData,
    policyId ? { policyId } : 'skip'
  );

  const selectedType = useQuery(
    api['insurance/types'].getOrgType,
    selectedTypeId ? { typeId: selectedTypeId } : 'skip'
  );

  const selectedTypeWithSections = useQuery(
    api['insurance/types'].getByIdWithSections,
    selectedTypeId ? { typeId: selectedTypeId } : 'skip'
  );

  const providers = useQuery(
    api['insurance/providers'].list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );

  // Providers with benefit plans for selected insurance type
  const providerIdsForType = useQuery(
    api['insurance/benefitPlans'].listProvidersByType,
    currentOrg && selectedTypeId
      ? { orgId: currentOrg._id, insuranceTypeId: selectedTypeId }
      : 'skip'
  );

  // Benefit plans for selected provider + insurance type
  const providerFieldValue = form.getFieldValue('Provider');
  const benefitPlansQuery = useQuery(
    api['insurance/benefitPlans'].listByProviderAndType,
    currentOrg && selectedTypeId && providerFieldValue
      ? {
          orgId: currentOrg._id,
          providerId: providerFieldValue as Id<'insuranceProviders'>, // AS_JUSTIFIED: value from Provider select populated with provider IDs
          insuranceTypeId: selectedTypeId,
        }
      : 'skip'
  );
  const benefitPlans = benefitPlansQuery ?? [];

  // Query existing benefit plan assignment for editing
  const existingAssignment = useQuery(
    api['insurance/benefitAssignments'].getByPolicy,
    policyId ? { policyId } : 'skip'
  );

  const assignPlanToPolicy = useMutation(api['insurance/benefitAssignments'].assignPlanToPolicy);

  const policySource: TPolicySource = employeePolicyForm.policySource;

  const formTemplate = useQuery(
    api.insurance.policyFormTemplates.getByProviderAndSource,
    selectedTypeId && currentOrg && providerFieldValue
      ? {
          orgId: currentOrg._id,
          insuranceTypeId: selectedTypeId,
          policySource,
          providerId: providerFieldValue as Id<'insuranceProviders'>,
        } // AS_JUSTIFIED: value from Provider select populated with provider IDs
      : 'skip'
  );

  const applicationData = useQuery(
    api.insurance.policyApplications.getApplicationData,
    existingPolicy?.templateId ? { policyId: existingPolicy._id } : 'skip'
  );

  const createPolicy = useMutation(api['insurance/policies'].create);
  const updatePolicy = useMutation(api['insurance/policies'].update);
  const createDriver = useMutation(api['insurance/drivers'].create);
  const removeDriver = useMutation(api['insurance/drivers'].remove);
  const createEmployeeForTeamMember = useMutation(
    api['insurance/policies'].createEmployeeForTeamMember
  );

  // Initialize form when editing an existing policy
  useEffect(() => {
    if (!isEditing || existingPolicy === undefined || existingPolicy === null) return;
    form.initFromExisting({
      policyNumber: existingPolicy.policyNumber,
      customerName: existingPolicy.customerName,
      status: existingPolicy.status,
      invoiceStatus: existingPolicy.invoiceStatus,
      partiallyPaidAmount: existingPolicy.partiallyPaidAmount,
      premium: existingPolicy.premium,
      policyStartDate: existingPolicy.policyStartDate,
      policyExpireDate: existingPolicy.policyExpireDate,
      policyDuration: existingPolicy.policyDuration,
      fieldValues: existingPolicy.fieldValues,
    });
    if (existingPolicy.drivers) {
      driverForm.initFromExisting(existingPolicy.drivers, existingPolicy.mainDriverId);
    }
    setSelectedTypeIdRaw(existingPolicy.insuranceTypeId);
    // Initialize benefit plan from existing assignment
    if (existingAssignment && existingAssignment.length > 0) {
      setSelectedBenefitPlanId(existingAssignment[0].planId);
    }
  }, [isEditing, existingPolicy, existingAssignment]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-add first driver when type supports drivers and creating new policy
  useEffect(() => {
    if (isEditing) return;
    if (!selectedType?.supportsDrivers) return;
    if (driverForm.drivers.length > 0) return;
    driverForm.addDriver();
  }, [selectedType?.supportsDrivers]); // eslint-disable-line react-hooks/exhaustive-deps

  const [lastPopulatedEmployeeId, setLastPopulatedEmployeeId] = useState<string | null>(null);

  // Initialize employee policy form from existing policy data
  useEffect(() => {
    if (!isEditing || existingPolicy === undefined || existingPolicy === null) return;
    if (
      existingPolicy.policySource === 'personal' ||
      existingPolicy.policySource === 'company_employer'
    ) {
      employeePolicyForm.setPolicySource(existingPolicy.policySource);
    }
    if (existingPolicy.companyId) {
      employeePolicyForm.setSelectedCompanyId(existingPolicy.companyId);
    }
    if (existingPolicy.companyEmployeeId) {
      employeePolicyForm.setSelectedEmployeeId(existingPolicy.companyEmployeeId);
      setLastPopulatedEmployeeId(existingPolicy.companyEmployeeId);
    }
  }, [isEditing, existingPolicy?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-populate form when a new employee is selected and their data loads
  useEffect(() => {
    if (!employeePolicyForm.employeeData) return;
    if (lastPopulatedEmployeeId === employeePolicyForm.employeeData._id) return;
    if (employeePolicyForm.selectedEmployeeId !== employeePolicyForm.employeeData._id) return;

    const dynamicFieldLabels = dynamicFields.map(f => f.label);
    const supportsDrivers = selectedType?.supportsDrivers === true;

    // EMPLOYEE_FIELD_MAP in useEmployeePolicyForm only passes valid IDriverFormData keys/license fields at runtime;
    // populateFormFromEmployee expects string-typed params, useDriverForm uses keyof-narrowed params — cast resolves the contravariance gap
    employeePolicyForm.populateFormFromEmployee(
      form,
      driverForm as unknown as Parameters<typeof employeePolicyForm.populateFormFromEmployee>[1], // AS_JUSTIFIED: structural match is guaranteed by EMPLOYEE_FIELD_MAP constraint; only keyof IDriverFormData values are ever passed
      supportsDrivers,
      dynamicFieldLabels
    );
    setLastPopulatedEmployeeId(employeePolicyForm.employeeData._id);
  }, [employeePolicyForm.employeeData?._id, employeePolicyForm.selectedEmployeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-populate customer name for team members (no employeeData available).
  // form.setCustomerName is a useState setter (stable); destructured to avoid
  // including the unstable form object in deps.
  const { setCustomerName } = form;
  useEffect(() => {
    if (!employeePolicyForm.selectedIsTeamMember) return;
    if (!employeePolicyForm.selectedEmployeeId) return;
    if (lastPopulatedEmployeeId === employeePolicyForm.selectedEmployeeId) return;
    const entry = employeePolicyForm.employees.find(
      e => e._id === employeePolicyForm.selectedEmployeeId
    );
    if (!entry) return;
    setCustomerName(entry.name);
    setLastPopulatedEmployeeId(employeePolicyForm.selectedEmployeeId);
  }, [
    employeePolicyForm.selectedIsTeamMember,
    employeePolicyForm.selectedEmployeeId,
    employeePolicyForm.employees,
    lastPopulatedEmployeeId,
    setCustomerName,
  ]);

  const typeList: IOrgInsuranceType[] = orgTypes ?? [];
  const isLoading = orgLoading || (isEditing && existingPolicy === undefined);
  const allProviders: IInsuranceProvider[] = providers ?? [];
  const activeProviders = allProviders.filter(p => p.isActive !== false);
  const providerList: IInsuranceProvider[] = providerIdsForType
    ? activeProviders.filter(p => providerIdsForType.includes(p._id))
    : activeProviders;

  const wizardCompanyEmployeeId: Id<'companyEmployees'> | undefined =
    existingPolicy?.companyEmployeeId ??
    (!employeePolicyForm.selectedIsTeamMember && employeePolicyForm.selectedEmployeeId
      ? (employeePolicyForm.selectedEmployeeId as Id<'companyEmployees'>) // AS_JUSTIFIED: non-team-member selectedEmployeeId comes from employee picker populated with companyEmployee IDs
      : undefined);

  const driverDynamicLabels = new Set([
    'Driver Full Name',
    'Driver Birthday',
    'Driver Nationality',
    'Driver License Issue Country',
    'Driver License Issue Date',
    'Driver License Front',
    'Driver License Back',
  ]);

  const dynamicFields: IInsuranceField[] = selectedType
    ? (selectedType.fields as IInsuranceField[]).filter(
        // AS_JUSTIFIED: Convex query returns typed field array matching IInsuranceField schema
        (f: IInsuranceField) =>
          f.fieldType !== 'provider' &&
          !(selectedType.supportsDrivers && driverDynamicLabels.has(f.label))
      )
    : [];

  const fieldSections: IFieldSectionConfig[] = [
    {
      title: 'Coverage & Options',
      icon: ShieldCheck,
      labels: ['Vehicle registered on customer name', 'Any Driver option'],
      cols: 3,
    },
    {
      title: 'Customer Details',
      icon: MapPin,
      labels: ['Home address', 'Postal code', 'City', 'Phone number', 'Email address'],
      fullWidthLabels: ['Home address'],
    },
    {
      title: 'Vehicle Information',
      icon: Car,
      labels: ['Car registration number', 'Car model', 'Car market price'],
      cols: 3,
    },
  ];

  const groupedSections: IFieldSectionWithFields[] = fieldSections
    .map(section => ({
      ...section,
      fields: section.labels
        .map(label => dynamicFields.find(f => f.label.toLowerCase() === label.toLowerCase()))
        .filter((f): f is IInsuranceField => f !== undefined),
    }))
    .filter(g => g.fields.length > 0);

  const groupedLabels = new Set(groupedSections.flatMap(s => s.fields.map(f => f.label)));
  const ungroupedFields = dynamicFields.filter(
    f => !groupedLabels.has(f.label) && f.label !== 'Claim history'
  );

  const handleDownloadCertificate = async () => {
    if (!certificateData) return;
    const blob = await generateCertificatePdf(certificateData);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `certificate-${certificateData.policyholder?.policyNumber ?? 'policy'}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    if (!form.policyNumber.trim()) {
      setSubmitError('Policy number is required');
      return;
    }
    if (!selectedTypeId) {
      setSubmitError('Please select an insurance type');
      return;
    }
    if (!currentOrg) {
      setSubmitError('No organisation found');
      return;
    }

    for (const field of dynamicFields) {
      const val = form.getFieldValue(field.label);
      if (!val) continue;
      const labelLower = field.label.toLowerCase();
      if (labelLower.includes('email') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        setSubmitError(`Invalid email address in "${field.label}"`);
        return;
      }
      if (labelLower.includes('phone') && !/^\+\d[\d\s\-()]{6,}$/.test(val)) {
        setSubmitError(`"${field.label}" must include country code (e.g. +1 234 567 8900)`);
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const supportsDrivers = selectedType?.supportsDrivers === true;

      // Build fieldValues: new-schema path (sections[]) vs legacy path
      const sectionsForSave = selectedTypeWithSections?.sections ?? [];
      const builtFieldValues: IPolicyFieldValueV2[] = (() => {
        if (sectionsForSave.length === 0) {
          return form.toFieldValues();
        }
        // New-schema path: iterate all section fields, emit fieldKey + fieldLabel + value
        const result: IPolicyFieldValueV2[] = [];
        const currentKeys = new Set<string>();
        for (const section of sectionsForSave) {
          for (const f of section.fields) {
            currentKeys.add(f.fieldKey);
            const rawValue = form.getFieldValue(f.label);
            const storageIds = form.getFieldStorageIds(f.label);
            result.push({
              fieldKey: f.fieldKey,
              fieldLabel: f.label,
              value: rawValue !== '' ? rawValue : undefined,
              storageIds: storageIds.length > 0 ? storageIds : undefined,
            });
          }
        }
        // Preserve archived/legacy fieldValues not in current section keys
        for (const existing of existingPolicy?.fieldValues ?? []) {
          if (!existing.fieldKey || !currentKeys.has(existing.fieldKey)) {
            result.push({
              // AS_JUSTIFIED: IPolicyFieldValueV2 is a superset of IPolicyFieldValue — fieldKey is optional in both
              fieldKey: existing.fieldKey,
              fieldLabel: existing.fieldLabel,
              value: existing.value,
              storageIds: existing.storageIds,
            });
          }
        }
        return result;
      })();

      let resolvedEmployeeId: Id<'companyEmployees'> | undefined;
      if (employeePolicyForm.selectedEmployeeId) {
        if (employeePolicyForm.selectedIsTeamMember && employeePolicyForm.selectedCompanyId) {
          resolvedEmployeeId = await createEmployeeForTeamMember({
            companyId: employeePolicyForm.selectedCompanyId as Id<'companies'>, // AS_JUSTIFIED: selectedCompanyId from company picker
            userId: employeePolicyForm.selectedEmployeeId as Id<'users'>, // AS_JUSTIFIED: team member _id is userId from listEmployeesForPolicyForm
          });
        } else {
          resolvedEmployeeId = employeePolicyForm.selectedEmployeeId as Id<'companyEmployees'>; // AS_JUSTIFIED: value from employee picker populated with companyEmployee IDs
        }
      }

      if (isEditing && policyId) {
        await updatePolicy({
          policyId,
          policyNumber: form.policyNumber,
          customerName:
            supportsDrivers && driverForm.drivers[0]?.fullName
              ? driverForm.drivers[0].fullName
              : form.customerName || undefined,
          status: form.status,
          invoiceStatus: form.invoiceStatus,
          partiallyPaidAmount:
            form.invoiceStatus === 'partially_paid' ? form.partiallyPaidAmount : undefined,
          premium: form.premium,
          policyStartDate: form.policyStartDate || undefined,
          policyExpireDate: form.policyExpireDate || undefined,
          policyDuration: form.policyDuration || undefined,
          fieldValues: builtFieldValues,
          companyEmployeeId: resolvedEmployeeId,
          policySource: employeePolicyForm.policySource,
        });

        if (supportsDrivers && existingPolicy?.drivers) {
          for (const oldDriver of existingPolicy.drivers) {
            await removeDriver({ driverId: oldDriver._id });
          }
        }
        if (supportsDrivers) {
          const payload = driverForm.toDriversPayload();
          const newDriverIds: Id<'policyDrivers'>[] = [];
          for (const d of payload) {
            const driverId = await createDriver({
              policyId,
              fullName: d.fullName,
              birthday: d.birthday,
              nationality: d.nationality,
              idNumber: d.idNumber,
              licenseIssueCountry: d.licenseIssueCountry,
              licenseIssueDate: d.licenseIssueDate,
              licenseFrontStorageId: d.licenseFrontStorageId,
              licenseBackStorageId: d.licenseBackStorageId,
            });
            newDriverIds.push(driverId);
          }
          const mainId =
            driverForm.mainDriverIndex !== null
              ? newDriverIds[driverForm.mainDriverIndex]
              : undefined;
          await updatePolicy({ policyId, mainDriverId: mainId });
        }
      } else {
        const newPolicyId = await createPolicy({
          orgId: currentOrg._id,
          insuranceTypeId: selectedTypeId,
          policyNumber: form.policyNumber,
          customerName:
            supportsDrivers && driverForm.drivers[0]?.fullName
              ? driverForm.drivers[0].fullName
              : form.customerName || undefined,
          status: form.status,
          invoiceStatus: form.invoiceStatus,
          partiallyPaidAmount:
            form.invoiceStatus === 'partially_paid' ? form.partiallyPaidAmount : undefined,
          premium: form.premium,
          policyStartDate: form.policyStartDate || undefined,
          policyExpireDate: form.policyExpireDate || undefined,
          policyDuration: form.policyDuration || undefined,
          fieldValues: builtFieldValues,
          companyEmployeeId: resolvedEmployeeId,
          policySource: employeePolicyForm.policySource,
        });

        if (supportsDrivers) {
          const payload = driverForm.toDriversPayload();
          const newDriverIds: Id<'policyDrivers'>[] = [];
          for (const d of payload) {
            const driverId = await createDriver({
              policyId: newPolicyId,
              fullName: d.fullName,
              birthday: d.birthday,
              nationality: d.nationality,
              idNumber: d.idNumber,
              licenseIssueCountry: d.licenseIssueCountry,
              licenseIssueDate: d.licenseIssueDate,
              licenseFrontStorageId: d.licenseFrontStorageId,
              licenseBackStorageId: d.licenseBackStorageId,
            });
            newDriverIds.push(driverId);
          }
          const mainId =
            driverForm.mainDriverIndex !== null
              ? newDriverIds[driverForm.mainDriverIndex]
              : undefined;
          if (mainId) {
            await updatePolicy({ policyId: newPolicyId, mainDriverId: mainId });
          }
        }

        // Assign benefit plan if selected
        if (selectedBenefitPlanId && currentOrg) {
          await assignPlanToPolicy({
            orgId: currentOrg._id,
            policyId: newPolicyId,
            planId: selectedBenefitPlanId as Id<'benefitPlans'>, // AS_JUSTIFIED: value from Select populated with benefitPlan IDs
          });
        }
      }

      // For edit mode: update benefit plan assignment if changed
      if (isEditing && policyId && selectedBenefitPlanId && currentOrg) {
        const currentPlanId = existingAssignment?.[0]?.planId;
        if (selectedBenefitPlanId !== currentPlanId) {
          await assignPlanToPolicy({
            orgId: currentOrg._id,
            policyId,
            planId: selectedBenefitPlanId as Id<'benefitPlans'>, // AS_JUSTIFIED: value from Select populated with benefitPlan IDs
          });
        }
      }

      navigate(-1);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save policy');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    // Org
    currentOrg,
    isInOrg,
    orgLoading,
    // Loading / submission state
    isLoading,
    isSubmitting,
    submitError,
    // Type / provider data
    typeList,
    providerList,
    selectedTypeId,
    setSelectedTypeId,
    selectedType,
    selectedTypeWithSections,
    // Form sub-hooks
    form,
    driverForm,
    employeePolicyForm,
    // Policy source + wizard
    policySource,
    wizardPhase,
    setWizardPhase,
    wizardCompanyEmployeeId,
    // Existing policy
    existingPolicy,
    certificateData,
    // Template & application
    formTemplate,
    applicationData,
    // Derived field data
    dynamicFields,
    groupedSections,
    ungroupedFields,
    // Benefit plans
    benefitPlans,
    selectedBenefitPlanId,
    setSelectedBenefitPlanId,
    // Handlers
    handleSubmit,
    handleDownloadCertificate,
    navigate,
  };
}
